
import { toast } from "@/components/ui/sonner";
import { logger } from "@/lib/logger";

/**
 * Milliseconds of continuous silence before nudging the user that they can click to finish.
 *
 * 5 seconds is long enough to strongly suggest the user has finished a thought,
 * while still feeling responsive. This is a UX-driven value, not a browser limit.
 */
const SILENCE_HINT_MS = 5000;
/**
 * Milliseconds of continuous silence before automatically stopping the recording.
 *
 * 10 seconds prevents orphaned sessions if a user walks away, without cutting off
 * normal pauses in speech. This was chosen as a conservative default and can be
 * tuned, but should remain clearly longer than SILENCE_HINT_MS.
 */
const SILENCE_STOP_MS = 10000;
/**
 * Milliseconds with no speech detected at the start before auto-stopping.
 *
 * Mirrors SILENCE_STOP_MS so that "no speech at all" is treated consistently with
 * "very long silence". This is a UX safeguard, not a hard browser constraint.
 */
const NO_SPEECH_STOP_MS = 10000;
/**
 * Delay in milliseconds before restarting speech recognition after it ends.
 *
 * 100 ms is effectively instantaneous from a user perspective but gives the
 * browser a short buffer to tear down and recreate the SpeechRecognition
 * instance, reducing the risk of tight restart loops or transient errors.
 */
const RESTART_DELAY_MS = 100;

interface VoiceRecorderOptions {
  onTranscription: (text: string) => void;
  onRecordingStart?: () => void;
  onRecordingStop?: () => void;
  onError?: (error: Error) => void;
  /** Called after 5s of silence to nudge user to click to finish (e.g. show tooltip/toast) */
  onSilenceHint?: () => void;
  /** Called with live transcript (final + interim) as words stream in */
  onStreamingTranscript?: (text: string) => void;
}

class VoiceRecorder {
  private recognition: SpeechRecognition | undefined = undefined;
  private isRecording: boolean = false;
  private isUserStopped: boolean = false;
  private options: VoiceRecorderOptions;
  private accumulatedTranscript: string = "";
  private lastResultTime: number = 0;
  private startTime: number = 0;
  private silenceHintTimeout: ReturnType<typeof setTimeout> | null = null;
  private silenceStopTimeout: ReturnType<typeof setTimeout> | null = null;
  private restartTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(options: VoiceRecorderOptions) {
    this.options = options;
  }

  /**
   * Check if the browser supports speech recognition
   */
  private checkBrowserSupport(): SpeechRecognition | undefined {
    if (typeof window !== "undefined") {
      const isSecureContext =
        window.isSecureContext ||
        window.location.protocol === "https:" ||
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";

      logger.debug("Secure context check", {
        isSecureContext: window.isSecureContext,
        protocol: window.location.protocol,
        hostname: window.location.hostname,
        result: isSecureContext,
      });

      if (!isSecureContext) {
        logger.error("Web Speech API requires HTTPS or localhost");
        throw new Error(
          "Voice input requires HTTPS. Please access the site via https:// or localhost"
        );
      }

      const SpeechRecognitionAPI = (
        window.SpeechRecognition ??
        window.webkitSpeechRecognition ??
        (window as unknown as { mozSpeechRecognition?: unknown }).mozSpeechRecognition ??
        (window as unknown as { msSpeechRecognition?: unknown }).msSpeechRecognition
      ) as (new () => SpeechRecognition) | undefined;

      if (SpeechRecognitionAPI) {
        return new SpeechRecognitionAPI();
      }
    }
    return undefined;
  }

  /**
   * Request microphone permissions explicitly
   */
  private async requestMicrophonePermission(): Promise<boolean> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch (error) {
      logger.error("Microphone permission denied", { error });
      return false;
    }
  }

  private clearSilenceTimers() {
    if (this.silenceHintTimeout !== null) {
      clearTimeout(this.silenceHintTimeout);
      this.silenceHintTimeout = null;
    }
    if (this.silenceStopTimeout !== null) {
      clearTimeout(this.silenceStopTimeout);
      this.silenceStopTimeout = null;
    }
  }

  private scheduleSilenceTimers() {
    this.clearSilenceTimers();
    this.lastResultTime = Date.now();

    this.silenceHintTimeout = setTimeout(() => {
      this.silenceHintTimeout = null;
      if (this.isUserStopped || !this.isRecording) { return; }
      if (this.options.onSilenceHint) {
        this.options.onSilenceHint();
      } else {
        toast.info("Click the mic when you're done speaking.", { duration: 4000 });
      }
    }, SILENCE_HINT_MS);

    this.silenceStopTimeout = setTimeout(() => {
      this.silenceStopTimeout = null;
      if (this.isUserStopped || !this.isRecording) { return; }
      logger.info("Stopping voice recording after 10s silence");
      this.isUserStopped = true;
      this.finishAndStop();
    }, SILENCE_STOP_MS);
  }

  private finishAndStop() {
    this.clearSilenceTimers();
    if (this.restartTimeout !== null) {
      clearTimeout(this.restartTimeout);
      this.restartTimeout = null;
    }
    if (this.recognition && this.isRecording) {
      // Stop recognition — onend will fire and deliver the transcript.
      this.recognition.stop();
    } else {
      // Recognition already stopped, deliver directly.
      this.isRecording = false;
      if (this.accumulatedTranscript.trim()) {
        this.options.onTranscription(this.accumulatedTranscript.trim());
        toast.success("Voice captured");
      }
      if (this.options.onRecordingStop) {
        this.options.onRecordingStop();
      }
    }
  }

  public async startRecording() {
    try {
      logger.debug("VoiceRecorder.startRecording() called");

      if (this.isRecording) {
        logger.warn("Already recording, ignoring start request");

      /**
       * Enable continuous, streaming recognition.
       *
       * Setting `interimResults` to `true` causes the underlying
       * SpeechRecognition API to emit partial (interim) transcripts
       * as the user speaks, rather than only final results. This
       * significantly increases the frequency of `onresult` events
       * and can increase processing overhead in any consumers that
       * handle `onStreamingTranscript`.
       *
       * Callers listening to `onStreamingTranscript` SHOULD avoid
       * heavy work (e.g. complex React state updates, network calls)
       * on every event, and instead debounce/throttle or otherwise
       * batch updates to keep UI responsive.
       */
      }

      const recognition = this.checkBrowserSupport();
      if (!recognition) {
        throw new Error("Your browser doesn't support voice recognition");
      }
      logger.info("Browser support confirmed");

      const hasPermission = await this.requestMicrophonePermission();
      if (!hasPermission) {
        throw new Error(
          "Microphone permission denied. Please allow microphone access to use voice input."
        );
      }
      logger.info("Microphone permissions granted");

      this.recognition = recognition;
      this.isUserStopped = false;
      this.accumulatedTranscript = "";
      this.lastResultTime = 0;
      this.startTime = Date.now();
      this.clearSilenceTimers();

      this.recognition.continuous = true;
      this.recognition.interimResults = true;
      this.recognition.lang = "en-US";
      this.recognition.maxAlternatives = 1;

      logger.debug("Speech recognition configured", {
        continuous: this.recognition.continuous,
        interimResults: this.recognition.interimResults,
        lang: this.recognition.lang,
      });

      this.recognition.onstart = () => {
        this.isRecording = true;
        if (this.options.onRecordingStart) {
          this.options.onRecordingStart();
        }
        logger.info("Voice recording started");
        toast.success("Listening… Click mic when done.", { duration: 2000 });
      };

      this.recognition.onresult = (event: SpeechRecognitionEvent) => {
        // Use event.resultIndex so we only process NEW results, not the full
        // history that the browser re-sends on every event in continuous mode.
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (!result) { continue; }
          const transcript = result[0]?.transcript ?? "";
          if (result.isFinal && transcript) {
            this.accumulatedTranscript = this.accumulatedTranscript
              ? `${this.accumulatedTranscript} ${transcript}`
              : transcript;
            this.scheduleSilenceTimers();
          }
        }

        if (this.options.onStreamingTranscript) {
          let displayText = this.accumulatedTranscript;
          // Append the current interim (last result when not yet final) for live preview.
          const lastResult = event.results[event.results.length - 1];
          if (lastResult && !lastResult.isFinal) {
            const interim = lastResult[0]?.transcript ?? "";
            if (interim) {
              displayText = displayText ? `${displayText} ${interim}` : interim;
            }
          }
          if (displayText.trim()) { this.options.onStreamingTranscript(displayText.trim()); }
        }
      };

      this.recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
        logger.error("Speech recognition error", { error: event.error });
        if (event.error === "aborted" && this.isUserStopped) {
          return;
        }
        const errorMessage = this.getErrorMessage(event.error);
        const error = new Error(errorMessage);
        if (this.options.onError) {
          this.options.onError(error);
        }
        toast.error(errorMessage);
        this.clearSilenceTimers();
        this.isRecording = false;
        this.isUserStopped = true;
        if (this.options.onRecordingStop) {
          this.options.onRecordingStop();
        }
      };

      this.recognition.onend = () => {
        this.isRecording = false;

        if (this.isUserStopped) {
          // User clicked stop (or auto-stop fired). Deliver transcript and notify UI.
          if (this.accumulatedTranscript.trim()) {
            this.options.onTranscription(this.accumulatedTranscript.trim());
            toast.success("Voice captured");
          }
          if (this.options.onRecordingStop) {
            this.options.onRecordingStop();
          }
          return;
        }

        if (!this.recognition) { return; }

        const now = Date.now();
        const hadResult = this.accumulatedTranscript.length > 0;
        const silenceDuration = hadResult ? now - this.lastResultTime : now - this.startTime;

        if (!hadResult && now - this.startTime >= NO_SPEECH_STOP_MS) {
          logger.info("Stopping after no speech detected");
          this.isUserStopped = true;
          this.finishAndStop();
          return;
        }

        if (hadResult && silenceDuration >= SILENCE_STOP_MS) {
          this.isUserStopped = true;
          this.finishAndStop();
          return;
        }

        // Browser ended the session (e.g. short silence) but user hasn't stopped —
        // restart automatically to keep recording.
        logger.debug("Recognition ended, restarting to continue listening");
        this.isRecording = true; // restore before restart
        this.restartTimeout = setTimeout(() => {
          this.restartTimeout = null;
          if (!this.isRecording || this.isUserStopped || !this.recognition) { return; }
          try {
            this.recognition.start();
          } catch (e) {
            logger.warn("Restart failed", { error: e });
            this.isRecording = false;
            this.isUserStopped = true;
            this.finishAndStop();
          }
        }, RESTART_DELAY_MS);
      };

      logger.debug("Starting speech recognition");
      this.recognition.start();
      logger.debug("Speech recognition start() called successfully");
    } catch (error) {
      logger.error("Error starting voice recording", { error });
      const errorMessage =
        error instanceof Error ? error.message : "Could not start voice recording";
      if (this.options.onError) {
        this.options.onError(error instanceof Error ? error : new Error(errorMessage));
      }
      toast.error(errorMessage);
      this.isRecording = false;
      if (this.options.onRecordingStop) {
        this.options.onRecordingStop();
      }
    }
  }

  private getErrorMessage(errorCode: string): string {
    switch (errorCode) {
      case "no-speech":
        return "No speech was detected. Click the mic and try again.";
      case "aborted":
        return "Voice recording was aborted. Please try again.";
      case "audio-capture":
        return "Could not access microphone. Please check your device settings.";
      case "network":
        return "Network error occurred. Please check your internet connection.";
      case "not-allowed":
        return "Microphone access denied. Please allow microphone access in your browser settings.";
      case "service-not-allowed":
        return "Speech recognition service is not allowed. Please try another browser.";
      case "bad-grammar":
        return "Speech grammar error. Please try again.";
      case "language-not-supported":
        return "Language not supported. Please try English.";
      default:
        return "An error occurred during voice recognition. Please try again.";
    }
  }

  public stopRecording() {
    this.isUserStopped = true;
    if (this.restartTimeout !== null) {
      clearTimeout(this.restartTimeout);
      this.restartTimeout = null;
    }
    this.clearSilenceTimers();
    if (this.recognition && this.isRecording) {
      this.recognition.stop();
      // onend will fire next and deliver the transcript + call onRecordingStop.
    } else {
      // Recognition already stopped — deliver directly.
      if (this.accumulatedTranscript.trim()) {
        this.options.onTranscription(this.accumulatedTranscript.trim());
        toast.success("Voice captured");
      }
      if (this.options.onRecordingStop) {
        this.options.onRecordingStop();
      }
    }
  }
}

export default VoiceRecorder;
